var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
// SPDX-License-Identifier: AGPL-3.0-only
import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import { Alert, Box, Button, CircularProgress, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Dialog } from "@sequentech/ui-essentials";
import { useTranslation } from "react-i18next";
import { FORENSIC_NETWORK_DETAIL } from "@/queries/ForensicNetwork";
import { IPermissions } from "@/types/keycloak";
import { AdminDateTime } from "@/components/AdminDateTime";
import { ForensicPseudonym, ForensicCountry, ForensicBallotCount } from "./ForensicFields";
export var ForensicAddressDialog = function (_a) {
    var electionEventId = _a.electionEventId, electionId = _a.electionId, electionLabel = _a.electionLabel, _b = _a.shared, shared = _b === void 0 ? false : _b, ip = _a.ip, onClose = _a.onClose;
    var t = useTranslation().t;
    var _c = useState([null]), cursors = _c[0], setCursors = _c[1];
    var _d = useQuery(FORENSIC_NETWORK_DETAIL, {
        variables: {
            electionEventId: electionEventId,
            electionId: electionId,
            ip: ip,
            cursor: cursors[cursors.length - 1],
            limit: 25,
        },
        fetchPolicy: "no-cache",
        context: { headers: { "x-hasura-role": IPermissions.FORENSIC_READ } },
    }), data = _d.data, loading = _d.loading, error = _d.error, refetch = _d.refetch;
    var detail = data === null || data === void 0 ? void 0 : data.forensic_network_detail.payload;
    return (_jsx(Dialog, { open: true, fullWidth: true, maxWidth: "md", variant: "info", title: t("ballotPrivacy.forensic.detailTitle", { ip: ip }), ok: t("ballotPrivacy.close"), handleClose: onClose, className: "forensic-address-dialog", children: _jsx(Stack, { spacing: 2, sx: { minWidth: 0 }, children: loading ? (_jsx(Box, { sx: { display: "flex", justifyContent: "center", py: 4 }, children: _jsx(CircularProgress, { "aria-label": t("common.label.loadingData") }) })) : error || !detail ? (_jsx(Alert, { severity: "error", action: _jsx(Button, { onClick: function () { return void refetch().catch(function () { return undefined; }); }, children: t("ballotPrivacy.retry") }), children: t("ballotPrivacy.forensic.error") })) : (_jsxs(_Fragment, { children: [_jsx(Box, { component: "dl", sx: {
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "96px minmax(0, 1fr)",
                                sm: "96px minmax(0, 1fr) 96px minmax(0, 1fr)",
                            },
                            columnGap: 2,
                            rowGap: 1,
                            alignItems: "baseline",
                            m: 0,
                        }, children: [
                            [
                                t("ballotPrivacy.forensic.address"),
                                _jsx(Box, { component: "span", sx: { fontFamily: "monospace", overflowWrap: "anywhere" }, children: detail.ip }),
                            ],
                            [
                                t("dashboard.ipAddress.country"),
                                _jsx(ForensicCountry, { code: detail.country, showName: true }),
                            ],
                            [
                                t("ballotPrivacy.forensic.ballots"),
                                _jsx(Box, { sx: { display: "inline-flex" }, children: _jsx(ForensicBallotCount, { count: detail.vote_count, shared: shared }) }),
                            ],
                            [
                                t("dashboard.ipAddress.ElectionName"),
                                electionLabel !== null && electionLabel !== void 0 ? electionLabel : t("ballotPrivacy.forensic.allElections"),
                            ],
                            [
                                t("ballotPrivacy.forensic.first"),
                                _jsx(AdminDateTime, { value: detail.first_ballot, event: electionEventId, seconds: true }),
                            ],
                            [
                                t("ballotPrivacy.forensic.last"),
                                _jsx(AdminDateTime, { value: detail.last_ballot, event: electionEventId, seconds: true }),
                            ],
                        ].map(function (_a, index) {
                            var label = _a[0], value = _a[1];
                            return (_jsxs(React.Fragment, { children: [_jsx(Typography, { component: "dt", variant: "caption", color: "text.secondary", children: label }), _jsx(Typography, { component: "dd", variant: "body2", sx: { m: 0, overflowWrap: "anywhere" }, children: value })] }, index));
                        }) }), detail.ballots.length ? (_jsx(TableContainer, { sx: {
                            maxHeight: 350,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                        }, children: _jsxs(Table, { size: "small", stickyHeader: true, sx: {
                                "tableLayout": "fixed",
                                "& .MuiTableCell-root": {
                                    px: { xs: 1, sm: 1.5 },
                                    py: 1,
                                    overflowWrap: "anywhere",
                                },
                                "& th": {
                                    bgcolor: "#F7F9FC",
                                    color: "#475569",
                                    fontWeight: 600,
                                },
                                "& tr:last-child td": { borderBottom: 0 },
                            }, children: [_jsx(TableHead, { children: _jsxs(TableRow, { children: [_jsx(TableCell, { children: t("ballotPrivacy.pseudonym") }), _jsx(TableCell, { children: t("ballotPrivacy.ballotId") }), _jsx(TableCell, { children: t("ballotPrivacy.castAt") })] }) }), _jsx(TableBody, { children: detail.ballots.map(function (ballot) {
                                        var _a;
                                        return (_jsxs(TableRow, { children: [_jsx(TableCell, { children: _jsx(ForensicPseudonym, { code: ballot.pseudonym }) }), _jsx(TableCell, { sx: {
                                                        fontFamily: "monospace",
                                                        fontSize: 13,
                                                        fontWeight: 600,
                                                    }, children: (_a = ballot.ballot_id) !== null && _a !== void 0 ? _a : "—" }), _jsx(TableCell, { sx: { fontSize: 12 }, children: _jsx(AdminDateTime, { value: ballot.cast_at, event: electionEventId, seconds: true }) })] }, "".concat(ballot.ballot_id, "-").concat(ballot.cast_at)));
                                    }) })] }) })) : (_jsx(Typography, { variant: "body2", color: "text.secondary", sx: { py: 3 }, children: t("ballotPrivacy.forensic.empty") })), cursors.length > 1 || detail.next_cursor ? (_jsxs(Stack, { direction: "row", justifyContent: "flex-end", spacing: 1, children: [_jsx(Button, { disabled: cursors.length === 1, onClick: function () { return setCursors(function (values) { return values.slice(0, -1); }); }, children: t("ballotPrivacy.previous") }), _jsx(Button, { disabled: !detail.next_cursor, onClick: function () {
                                    if (detail.next_cursor)
                                        setCursors(function (values) { return __spreadArray(__spreadArray([], values, true), [detail.next_cursor], false); });
                                }, children: t("ballotPrivacy.next") })] })) : null, _jsx(Alert, { icon: _jsx(LockOutlinedIcon, { fontSize: "small" }), severity: "info", variant: "outlined", sx: {
                            "bgcolor": "#F7F9FC",
                            "borderColor": "#DFE6EF",
                            "color": "text.primary",
                            "fontSize": 12,
                            "& .MuiAlert-icon": { color: "#64748B" },
                        }, children: t("ballotPrivacy.forensic.detailHelp") })] })) }) }));
};
